// Editing a channel's properties (shared/API.md §27; ChannelSettingsSheet.swift): ONE form where the
// desktop has three menu items. Only CHANGED fields go in the patch; no optimistic write — the
// server's answer IS the new record (the worker writes it into the store, so the header and the
// sidebar move by themselves). A DM/GM has exactly one property: its header.
import { useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { ChannelSettingsForm, type Failure, type Values } from '../../../core/channelSettingsForm';
import type { ChannelRecord } from '../../../core/records';
import { t } from '../../../i18n/i18n';
import { Button, ConfirmButton, Dialog, InlineStatus } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';

const FAILURE_TEXT: Record<Failure, string> = {
  invalid: 'settings.failedInvalid',
  notPermitted: 'settings.failedPermission',
  other: 'settings.failedOther',
};

export function SettingsDialog({ channel, name, permissions, onClose }: { channel: ChannelRecord; name: string; permissions: ReadonlySet<string>; onClose(): void }) {
  const shell = useShell();
  const original = useMemo(() => ChannelSettingsForm.values(channel), [channel.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const fields = useMemo(() => ChannelSettingsForm.editableFields(channel.type), [channel.type]);
  const [edited, setEdited] = useState<Values>(original);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const submittable = ChannelSettingsForm.isSubmittable(original, edited, fields) && !busy;
  const canConvert = ChannelSettingsForm.canConvertToPrivate(channel.type, channel.name, permissions);
  // Private → public, an admin's power (shared/API.md §27.4).
  const canConvertPublic = ChannelSettingsForm.canConvertToPublic(channel.type, permissions);

  const save = () => {
    if (!submittable) return;
    setBusy(true);
    setFailure(null);
    const patch = ChannelSettingsForm.patch(original, edited, fields);
    data.call('updateChannelProperties', channel.id, patch).then(
      (status) => {
        if (status === null) onClose();
        else {
          setFailure(ChannelSettingsForm.failure(status));
          setBusy(false);
        }
      },
      () => {
        setFailure('other');
        setBusy(false);
      },
    );
  };

  const convert = (toPublic = false) => {
    setBusy(true);
    setFailure(null);
    data.call(toPublic ? 'convertChannelToPublic' : 'convertChannelToPrivate', channel.id).then(
      (ok) => {
        if (ok) {
          shell.toast(t(toPublic ? 'settings.convertPublicConfirmMsg' : 'settings.convertConfirmMsg'));
          onClose();
        } else {
          setFailure('other');
          setBusy(false);
        }
      },
      () => {
        setFailure('other');
        setBusy(false);
      },
    );
  };

  const set = (field: keyof Values, max: number) => (value: string) => setEdited((v) => ({ ...v, [field]: ChannelSettingsForm.clamp(value, max) }));

  return (
    <Dialog
      title={fields.has('displayName') ? t('settings.title') : t('web.channelPanel.editHeader')}
      onClose={onClose}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" onClick={save} disabled={!submittable}>
            {t('settings.save')}
          </Button>
        </>
      }
    >
      <form
        className="cp-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {fields.has('displayName') ? (
          <label className="cp-field">
            <span>{t('settings.name')}</span>
            <input className="text-input" value={edited.displayName} onChange={(e) => set('displayName', ChannelSettingsForm.maxDisplayName)(e.target.value)} aria-label={t('settings.name')} placeholder={name} />
          </label>
        ) : null}
        {fields.has('purpose') ? (
          <label className="cp-field">
            <span>{t('channel.info.purpose')}</span>
            <textarea className="text-input" rows={2} value={edited.purpose} onChange={(e) => set('purpose', ChannelSettingsForm.maxPurpose)(e.target.value)} placeholder={t('web.channelPanel.purposePlaceholder')} />
          </label>
        ) : null}
        {fields.has('header') ? (
          <label className="cp-field">
            <span>{t('channel.info.header')}</span>
            <textarea className="text-input" rows={3} value={edited.header} onChange={(e) => set('header', ChannelSettingsForm.maxHeader)(e.target.value)} placeholder={t('web.channelPanel.headerPlaceholder')} />
          </label>
        ) : null}
        {failure ? <InlineStatus kind="error" text={t(FAILURE_TEXT[failure])} /> : null}
      </form>
      {/* Outside the form: the kit's two-tap button is a plain <button>, which a form would submit. */}
      {canConvert ? (
        <div className="cp-convert">
          <ConfirmButton icon="lock" label={t('settings.convert')} confirmLabel={t('settings.convertConfirm')} onConfirm={() => convert()} />
          <p>{t('settings.convertHint')}</p>
        </div>
      ) : null}
      {/* The way back, worded for what it does: the whole team can read the history from then on. */}
      {canConvertPublic ? (
        <div className="cp-convert">
          <ConfirmButton icon="hash" label={t('settings.convertPublic')} confirmLabel={t('settings.convertPublicConfirm')} onConfirm={() => convert(true)} />
          <p>{t('settings.convertPublicHint')}</p>
        </div>
      ) : null}
    </Dialog>
  );
}
